import React, { useEffect, useState, ReactElement, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useToasts } from 'react-toast-notifications';
import { ApolloProvider, ApolloClient, HttpLink, InMemoryCache } from '@apollo/client';

import { Section, Notification, DocumentUpload } from 'shared-library';
import { getApiUri } from 'packages/locations';
import {
  useCreateDocument,
  useGetActiveDocuments,
  useGetDocumentTypes,
  useDeactivateDocument,
  useGetLatestActiveDocument,
} from '../../hooks/documents';
import { DocumentRecord } from '../../hooks/documents/useGetActiveDocuments';
import { buildStudySelector } from '../../store/studies/selectors';
import { buildPatientSelector } from '../../store/patients/selectors';
import { selectSession } from '../../store/session/selectors';
import { DocumentStatus, DocumentTypeLabels } from '../../constants/documents';
import { getErrorMessage } from '../../utils/error';

const StudyDocuments: React.FC = () => {
  // State
  const [tableData, setTableData] = useState<(DocumentRecord & { id: string })[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Route params
  const { studyId: selectedStudyId } = useParams<{ studyId: string }>();

  // Selectors
  const selectStudy = buildStudySelector(selectedStudyId);
  const study = useSelector(selectStudy);
  const patient = useSelector(buildPatientSelector(study.patient));
  const { data: session } = useSelector(selectSession);

  // Hooks
  const { addToast } = useToasts();
  const { createDocument } = useCreateDocument();
  const { documents, loading: isFetchingDocument, error, refetch } = useGetActiveDocuments(
    selectedStudyId,
    study.poetryMetadata?.carePlanId,
  );
  const { fetchDocument } = useGetLatestActiveDocument();
  const {
    documentTypes,
    loading: isFetchingDocumentTypes,
    error: errorFetchingDocumentTypes,
  } = useGetDocumentTypes();

  const userRoles = session?.roles || [];
  const { deactivateDocument } = useDeactivateDocument();

  const fetchStudyDocument = async (typeId: string, _?: string, document?: DocumentRecord) => {
    try {
      const carePlanId = document?.carePlanId;
      const careActivityId = document?.careActivityId;

      const result = await fetchDocument(typeId, selectedStudyId, carePlanId, careActivityId);
      const activeDocument = result?.data?.getLatestActiveDocument;

      if (!activeDocument) {
        addToast(Notification.create('Document Fetch Failed', 'Document not found'), {
          appearance: 'error',
        });
        return null;
      }

      return {
        base64File: activeDocument.base64File,
        fileType: activeDocument.fileType,
      };
    } catch (err) {
      const errorMessage = getErrorMessage(err);
      addToast(Notification.create('Error', errorMessage), {
        appearance: 'error',
      });
      return null;
    }
  };

  const parseDataUrl = (dataUrl: string): { fileType: string; base64File: string } => {
    if (typeof dataUrl !== 'string') {
      throw new Error('Expected a Data-URL string');
    }
    const match = dataUrl.match(/^data:(.+);base64,(.+)$/);
    if (!match) {
      throw new Error('Invalid Data-URL format');
    }
    const [, fileType, base64File] = match;
    return { fileType, base64File };
  };

  const getDocumentStatusByType = (typeId: string): string => {
    const documentType = documentTypes?.find((type) => type.uuid === typeId);
    const typeName = documentType?.name;

    if (typeName && (typeName.includes('BLOOD') || typeName.includes('RADIOLOGY'))) {
      return DocumentStatus.DRAFT_READY;
    }

    return DocumentStatus.PENDING_QC;
  };

  const buildDocumentInput = (baseDocument: {
    patientId: string;
    typeId: string;
    status: string;
    name?: string;
    base64File?: string;
    fileType?: string;
    carePlanId?: string;
    careActivityId?: string;
  }) => {
    if (baseDocument.carePlanId && baseDocument.careActivityId) {
      return baseDocument;
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { carePlanId, careActivityId, ...studyDocument } = baseDocument;
    return {
      ...studyDocument,
      studyId: selectedStudyId,
    };
  };

  // Effects
  useEffect(() => {
    if (!isFetchingDocument && !error && documents && documents.length > 0) {
      const updatedDocuments = documents.map((document) => ({
        ...document,
        id: document.uuid,
        status: document.status,
      }));
      setTableData(updatedDocuments);
    }
  }, [isFetchingDocument, error, documents]);

  useEffect(() => {
    if (errorFetchingDocumentTypes) {
      addToast(
        Notification.create(
          'Fetching Document Types Failed',
          'There was an error fetching document types. Please try again.',
        ),
        { appearance: 'error' },
      );
    }
  }, [errorFetchingDocumentTypes]);

  const typeOptions = useMemo(
    () =>
      (documentTypes ?? []).map((documentType) => {
        const key = documentType.name;
        const label = DocumentTypeLabels[key] ?? documentType.name;
        return {
          value: documentType.uuid,
          label,
        };
      }),
    [documentTypes],
  );
  useEffect(() => {
    if (error) {
      addToast(
        Notification.create(
          'Fetching Documents Failed',
          'There was an error fetching documents. Please try again.',
        ),
        { appearance: 'error' },
      );
    }
  }, [error]);

  // Handlers
  const handleToggleStatus = async (document: DocumentRecord & { id: string }, status: string) => {
    try {
      setIsLoading(true);
      const documentInput = buildDocumentInput({
        patientId: patient.userId,
        typeId: document.typeId,
        carePlanId: document?.carePlanId,
        careActivityId: document?.careActivityId,
        status,
      });

      await createDocument({
        document: documentInput,
      });

      // If we reach here, the mutation was successful
      setTableData((prevData) =>
        prevData.map((item) => (item.id === document.id ? { ...item, status } : item)),
      );
    } catch (err) {
      // Handle all errors with unified error message utility
      const errorMessage = getErrorMessage(err);
      addToast(Notification.create('Status Update Failed', errorMessage), {
        appearance: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const uploadDocumentData = async (documentData) => {
    try {
      setIsLoading(true);

      let fileType;
      let base64File;
      try {
        const parsed = parseDataUrl(documentData.data);
        fileType = parsed.fileType;
        base64File = parsed.base64File;
      } catch (parseError) {
        addToast(Notification.create('Document Upload Failed', 'Invalid document format'), {
          appearance: 'error',
        });
        return;
      }

      const documentInput = buildDocumentInput({
        patientId: patient.userId,
        typeId: documentData.typeId,
        status: getDocumentStatusByType(documentData.typeId),
        name: documentData.name,
        base64File,
        fileType,
      });

      await createDocument({
        document: documentInput,
      });

      // If we reach here, the mutation was successful
      await refetch();
    } catch (err) {
      // Handle all errors with unified error message utility
      const errorMessage = getErrorMessage(err);
      addToast(Notification.create('Document Upload Failed', errorMessage), {
        appearance: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const deleteDocumentData = async (id: string, typeId?: string, document?: DocumentRecord) => {
    try {
      setIsLoading(true);
      const carePlanId = document?.carePlanId;
      const careActivityId = document?.careActivityId;

      const hasCarePlanData = carePlanId && careActivityId;
      await deactivateDocument({
        typeId,
        studyId: hasCarePlanData ? undefined : selectedStudyId,
        carePlanId,
        careActivityId,
      });
      setTableData((prevData) => prevData.filter((item) => item.uuid !== id));
    } catch (err) {
      const errorMessage = getErrorMessage(err);
      addToast(Notification.create('Document Deletion Failed', errorMessage), {
        appearance: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Section title="Medical Reports">
      {isFetchingDocumentTypes ? (
        'Loading...'
      ) : (
        <DocumentUpload
          isLoading={isLoading || isFetchingDocument}
          patient={patient}
          data={tableData}
          fetchDocument={fetchStudyDocument}
          onUploadDocument={uploadDocumentData}
          onDeleteDocument={deleteDocumentData}
          enableViewDocButton
          enableDeleteDocButton
          isStudyDocument
          onToggleStatus={handleToggleStatus}
          typeOptions={typeOptions}
          userRoles={userRoles}
        />
      )}
    </Section>
  );
};

const StudyDocumentsWithApollo = (): ReactElement => {
  const endpoint = getApiUri('CLINICAL_WORKFLOW');
  const client = useMemo(
    () =>
      new ApolloClient({
        link: new HttpLink({
          uri: endpoint,
          credentials: 'include',
        }),
        cache: new InMemoryCache(),
      }),
    [endpoint],
  );

  return (
    <ApolloProvider client={client}>
      <StudyDocuments />
    </ApolloProvider>
  );
};

export default StudyDocumentsWithApollo;
